import remap from './remap'
import create from 'zustand'
import store from '../store'
import { API } from './types'
import { State } from '../store/types'
import { useState, useCallback, useMemo } from 'react'
import useAxiosRequest from './useAxiosRequest'
import {
  useInfiniteQuery as useReactInfiniteQuery,
  UseInfiniteQueryResult
} from 'react-query'

const REMOVABLE_FILTERS = ['limit', 'offset', 'cursor']

const useInfiniteQuery = <Filters>(
  defaultFilters?: Filters,
  options?: API.QueryOptions,
  axiosConfig?: API.Config
) => {
  const axios = useAxiosRequest(axiosConfig)

  const useStore = create<State>(store)

  const { networkId, target } = useStore(
    useCallback((state) => {
      return {
        networkId: state.networkId,
        target: state.target
      }
    }, [])
  )

  const [filters, setFilters] = useState<Filters>(defaultFilters)

  const enabled = useMemo(() => {
    return [!!networkId, !!target, ...(options?.enabled || [])].every(
      (x) => !!x
    )
  }, [networkId, target, options?.enabled])

  const query = <Data>(request: API.QueryParams) => {
    const requestParams = {
      ...request,
      params: {
        ...(request?.params || {}),
        ...(filters || {})
      }
    }

    const getCursor = (lastPage) => {
      const firstResult = lastPage?.data?.[0]

      /**
       * If the first result isn't sponsored, it means we are on a "mixed"
       * network. If true, we are on an ad-only network
       */
      if (!firstResult?.sponsored) {
        /**
         * Check if the last result is sponsored. If true, we cannot use it
         * as a cursor outside an ad-only network
         */
        const lastResult = lastPage?.data?.[lastPage.data.length - 1]

        return lastResult?.sponsored
          ? lastPage?.data?.[lastPage.data.length - 2]
          : lastResult?.id
      }

      return lastPage?.data?.[lastPage?.data?.length - 1]?.id
    }

    const cacheKey = [...request?.cacheKey]

    const appliedFilters = Object.assign({}, filters || {})

    // remove filters that are useless for cache keys
    REMOVABLE_FILTERS.forEach((removable) => {
      delete appliedFilters?.[removable]
    })

    // if there is any filter remaining, pass it to the cache key
    if (!!Object.keys?.(appliedFilters).length) {
      cacheKey.push(appliedFilters)
    }

    const response = useReactInfiniteQuery(
      cacheKey,
      ({ pageParam }) => axios({ ...requestParams, pageParam }),
      {
        ...(options || {}),
        enabled,
        getNextPageParam: (lastPage, pages) => {
          if (!!lastPage) {
            const hasCount = Object?.keys?.(lastPage)?.includes('count')

            // Only fetch more if the last page had data
            if (!hasCount && !!lastPage?.data?.length) {
              const cursor = getCursor(lastPage)

              return { cursor, offset: 1 }
            }

            if (!!hasCount) {
              const offset = pages?.reduce((acc, page) => {
                acc += page?.data?.length || 0

                return acc
              }, 0)

              if (offset < lastPage?.count) {
                return { offset }
              }
            }

            return false
          }

          return false
        },
        // https://github.com/tannerlinsley/react-query/issues/3065
        select: (data: any) => {
          return data?.pages?.flatMap((page) => page?.data) || []
        }
      }
    )

    return remap<Data, Filters, UseInfiniteQueryResult<Data>>({
      response,
      filters,
      setFilters
    })
  }

  return query
}

export default useInfiniteQuery
